import { isFlexJobMode } from 'src/utils/jobMode';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { common } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import {
  useCreateCancellationNotices,
  useGetJobApplications,
} from 'src/pages/ProjectDetailsPage/api';
import DataTable from 'src/components/DataTable';
import { forceString } from 'src/utils/datetime';
import StudentLink from 'src/components/StudentLink';
import { REJECT_REASON } from 'src/pages/ProjectDetailsPage/api/queries/useGetRejectReason';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import ContractTooltip from '../../../ContractTooltip';
import { CancellationData } from '../../../JobCalendar';
import CancellationModal from '../CancellationModal/CancellationModal';
import { JobApplicationTabProps } from '../JobApplicationsAll/JobApplicationsAll';

import type { DefaultTheme } from '@mui/system';

export default function JobApplicationsBooked({
  job,
  jobUuid,
  filters,
  candidate,
  onCandidateSelect,
}: JobApplicationTabProps) {
  const isFlex = isFlexJobMode(job?.jobMode);
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [selected, setSelected] = useState<UCM.JobApplicationType | null>(null);
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: jobApplications, isLoading } = useGetJobApplications({
    jobUuid,
    type: 'booked',
    filters,
    params,
    enabled: Boolean(jobUuid),
  });

  const { mutate: cancellationNotices, isPending: isCancellationPending } =
    useCreateCancellationNotices({});

  const handleCancellation = useCallback((data: CancellationData) => {
    const { ...payload } = data;

    const filteredData = Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) => value != null && value !== '',
      ),
    );
    void cancellationNotices(filteredData);
  }, []);

  const pressCandidate = useCallback(
    (item: UCM.JobApplicationType | null) => {
      let candidate = (item as UCM.JobApplicationType | string) ?? null;

      if (!isFlex) {
        return setSelected(item);
      }

      onCandidateSelect?.(candidate);
    },
    [isFlex, onCandidateSelect],
  );

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: formatMessage(messages.applicantLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant) return '-';
          const { id, fullName, uuid } = item.applicant;
          return <StudentLink label={`${id} - ${fullName}`} uuid={uuid} />;
        },
      },
      {
        id: 'profile',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'cancelRate',
        label: formatMessage(messages.cancelRateLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.applicationsCancelledRatio) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.applicationsCancelledRatio}%
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'initialScore',
        label: formatMessage(messages.initialScoreColumnLabel),
        size: 'small',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.initialRatingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.initialRatingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'ratingScore',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'prescreeningRating',
        label: formatMessage(messages.preScreenLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { prescreeningRating } = item.applicant;

          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={1}>
                  {Object.entries(prescreeningRating).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="inherit"
                      textTransform="capitalize"
                    >
                      {key}: {value}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Typography variant="inherit">
                {prescreeningRating.average}
              </Typography>
            </Tooltip>
          );
        },
        align: 'center',
      },
      {
        id: 'briefingReadAt',
        label: formatMessage(messages.briefingLabel),
        render: (briefingReadAt: string | null) => {
          if (!briefingReadAt)
            return (
              <CloseOutlinedIcon
                sx={{ color: '#904A44', width: 24, height: 24 }}
              />
            );
          return (
            <Stack
              alignItems="center"
              flexDirection="row"
              justifyContent="center"
              gap={1}
            >
              <CheckOutlinedIcon
                sx={{ color: '#1E6A4F', width: 24, height: 24 }}
              />
              {forceString(briefingReadAt)}
            </Stack>
          );
        },
        align: 'center',
      },
      // {
      //   id: 'preJobQ',
      //   label: formatMessage(messages.preJobQLabel),
      //   // TODO - waiting for backend adjustements
      //   render: () => <Typography variant="inherit">-</Typography>,
      //   align: 'center',
      // },
      {
        id: 'contractSummary',
        label: formatMessage(messages.contractSummaryLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.contractSummary.status) return '-';
          return <ContractTooltip contract={item.contractSummary} />;
        },
        align: 'center',
      },
    ],
    [isFlex, selected?.applicant?.uuid],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const tableFooterSx = useCallback(
    (theme: DefaultTheme) => ({
      borderTop: `thin solid ${theme.palette.grey[200]}`,
    }),
    [],
  );

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        rows={jobApplications?.data}
        columns={columns}
        className={classes.tableContainer}
        loading={isLoading}
        activeRow={isFlex ? candidate : selected}
        pressRow={pressCandidate}
        checkActiveKey="applicant.id"
        onPaginate={handlePagination}
        pagination={jobApplications?.meta}
      />
      {!isFlex && (
        <Stack
          p={2}
          alignItems="flex-end"
          sx={tableFooterSx}
          bgcolor={common.white}
        >
          <CancellationModal
            type={REJECT_REASON.CANCELLATION_NOTICE}
            enabledFetch={Boolean(jobUuid) && Boolean(selected)}
            buttonLabel={formatMessage(messages.cancelRejectButton)}
            onConfirm={handleCancellation}
            isLoading={isCancellationPending}
            disabledButton={!selected || isCancellationPending}
            candidate={selected?.applicant}
            jobId={job?.id.toString() ?? ''}
          />
        </Stack>
      )}
    </Grid>
  );
}
